import { jwtDecode } from 'jwt-decode';

import { getSessionRealm, keycloakLogout } from '../auth/keycloak';

/** HECMIS roles, most privileged first. */
const HECMIS_ROLES = ['DG', 'MINISTER', 'BRD', 'SCHOLARSHIP_ADMIN', 'HLI_ADMIN', 'SCHOLARSHIP_OWNER', 'HEC_EVALUATOR', 'User'];

/**
 * The administrator role was renamed from "Admin" to "SCHOLARSHIP_ADMIN". Until every Keycloak
 * issues the new name, a token (or a password-login "role" claim) with the former one counts as the new
 * one, as the backend does. Remove once no environment issues "Admin".
 */
const FORMER_ROLE_NAMES = { Admin: 'SCHOLARSHIP_ADMIN' };
const canonicalRole = (role) => FORMER_ROLE_NAMES[role] ?? role;


/**
 * Get the JWT token from session storage
 * @returns {string|null} JWT token or null
 */
export const getToken = () => {
  try {
    // Mirrored from the Keycloak session by src/auth/keycloak.js
    return sessionStorage.getItem("token");
  } catch (error) {
    console.error('Error getting token:', error);
    return null;
  }
};

/**
 * Get decoded token data with expiration check
 * @returns {object|null} Decoded token payload or null if expired/invalid
 */
export const getDecodedToken = () => {
  const token = getToken();
  if (!token) return null;
  
  try {
    const decoded = jwtDecode(token);
    // Check if token is expired
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      console.warn('Token has expired');
      // A Keycloak session renews itself in the background, so an expired
      // mirrored token means "renew", not "sign out". restoreSession() in
      // App.js refreshes it; clearing the session here would log the user out
      // mid-request.
      if (getSessionRealm()) {
        return decoded;
      }
      // Log the auto-logout due to expiration
      try {
        fetch(`${process.env.REACT_APP_BASE_URL}hec/log-info/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {}); // Silently fail if API call fails
      } catch (error) {
        // Ignore errors during logout logging
      }
      sessionStorage.clear();
      return null;
    }
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Get user role from JWT token
 * IMPORTANT: This is for UI purposes only. Backend must validate all permissions.
 * The role comes from the JWT token which is signed by the backend and cannot be tampered with.
 * @returns {string|null} User role or null
 */
export const getUserRole = () => {
  const decoded = getDecodedToken();
  if (!decoded) return null;

  // Imported users carry a single "role" claim; Keycloak's own tokens do not.
  if (decoded.role) return canonicalRole(decoded.role);

  // Keycloak tokens carry realm roles as a list, mixed in with its own
  // built-ins (default-roles-*, offline_access, uma_authorization), so pick the
  // HECMIS one. Highest privilege wins if somebody holds more than one.
  const realmRoles = (decoded.realm_access?.roles || []).map(canonicalRole);
  return HECMIS_ROLES.find((role) => realmRoles.includes(role)) || null;
};

/**
 * Get user ID from JWT token
 * @returns {string|null} User ID or null
 */
export const getUserId = () => {
  const decoded = getDecodedToken();
  return decoded?.userId || null;
};

/**
 * Get user email from JWT token
 * @returns {string|null} User email or null
 */
export const getUserEmail = () => {
  const decoded = getDecodedToken();
  return decoded?.email || decoded?.sub || null;
};

/**
 * Get full user info from JWT token
 * @returns {object|null} User info object or null
 */
export const getUserInfo = () => {
  const decoded = getDecodedToken();
  if (!decoded) return null;
  
  return {
    userId: decoded.userId,
    email: decoded.email || decoded.sub,
    // Keycloak tokens carry given_name/family_name, password-login tokens firstName/lastName
    firstName: decoded.firstName || decoded.given_name,
    lastName: decoded.lastName || decoded.family_name,
    role: getUserRole(),
    roleId: decoded.roleId,
  };
};

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  return getDecodedToken() !== null;
};

/**
 * Check if user has a specific role
 * IMPORTANT: This is for UI purposes only. Backend must validate all permissions.
 * @param {string|string[]} roles - Role or array of roles to check
 * @returns {boolean} True if user has the role
 */
export const hasRole = (roles) => {
  const userRole = getUserRole();
  if (!userRole) return false;
  
  if (Array.isArray(roles)) {
    return roles.includes(userRole);
  }
  
  return userRole === roles;
};

/**
 * True when the caller holds any of the given roles. Variadic, so call sites read
 * hasAnyRole(ROLES.ADMIN, ROLES.DG). UI only: the backend checks every request.
 */
export const hasAnyRole = (...roles) => {
  const userRole = getUserRole();
  if (!userRole) return false;
  return roles.flat().includes(userRole);
};

/**
 * Check if user is admin or DG
 * @returns {boolean} True if user is admin or DG
 */
export const isAdminOrDG = () => {
  return hasRole(['SCHOLARSHIP_ADMIN', 'DG']);
};

/**
 * Logout user and clear session storage
 */
export const logout = async () => {
  // A Keycloak session must end at Keycloak too, otherwise the next login is
  // silently accepted by the still-open SSO session.
  if (getSessionRealm()) {
    try {
      const token = getToken();
      if (token) {
        await fetch(`${process.env.REACT_APP_BASE_URL}hec/log-info/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(err => console.error('Failed to log logout:', err));
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
    sessionStorage.clear();
    const redirected = await keycloakLogout();
    if (!redirected) {
      window.location.href = "/";
    }
    return;
  }

  try {
    const token = getToken();
    if (token) {
      // Call backend to log the logout
      await fetch(`${process.env.REACT_APP_BASE_URL}hec/log-info/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(err => console.error('Failed to log logout:', err));
    }
  } catch (error) {
    console.error('Error during logout:', error);
  } finally {
    sessionStorage.clear();
    window.location.href = "/";
  }
};

/**
 * Get authorization header for API requests
 * @returns {object} Headers object with Authorization
 */
export const getAuthHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// /**
//  * Get the stored user object (for backward compatibility)
//  * This reads the full user object from sessionStorage
//  * DEPRECATED: Use specific getter functions instead (getUserInfo, getUserRole, etc.)
//  * @returns {object|null} User object or null
//  */
// export const getStoredUserData = () => {
//   try {
//     const user = sessionStorage.getItem("user");
//     return user ? JSON.parse(user) : null;
//   } catch (error) {
//     console.error('Error parsing user data:', error);
//     return null;
//   }
// };